import { cssClasses } from './ReturnPolicyContent.styles';

/**
 * ReturnPolicyContent Utility Functions
 *
 * Helper functions for generating CSS classes and managing content structure
 * in the ReturnPolicyContent component.
 */

/**
 * Generates paragraph CSS classes based on type, position, and tenant
 *
 * @param {string} paragraphType - Type of paragraph ('primary', 'secondary', 'tertiary')
 * @param {number} sectionIndex - Index of the current section
 * @param {boolean} isChileanTenant - Whether the current tenant is Chilean
 * @param {boolean} isPeruvianTenant - Whether the current tenant is Peruvian
 * @returns {string} Space-separated CSS class names
 */
export const getParagraphClasses = (paragraphType, sectionIndex, isChileanTenant, isPeruvianTenant) => {
  const baseClasses = [cssClasses.paragraph.base];

  switch (paragraphType) {
    case 'primary':
      baseClasses.push(isChileanTenant ? cssClasses.paragraph.primaryLegacy : cssClasses.paragraph.primaryNew);
      if (sectionIndex === 0) baseClasses.push(cssClasses.paragraph.withTopMargin);
      if (isPeruvianTenant) baseClasses.push(cssClasses.paragraph.primaryOld);
      break;
    case 'secondary':
      baseClasses.push(isChileanTenant ? cssClasses.paragraph.secondaryLegacy : cssClasses.paragraph.secondaryNew);
      break;
    case 'tertiary':
      if (!isChileanTenant) baseClasses.push(cssClasses.paragraph.tertiaryNew);
      break;
    default:
      break;
  }

  return baseClasses.filter(Boolean).join(' ');
};

/**
 * Generates paragraph wrapper CSS classes based on tenant
 *
 * @param {boolean} isChileanTenant - Whether the current tenant is Chilean
 * @returns {string} Space-separated CSS class names
 */
export const getParagraphWrapperClasses = (isChileanTenant) => {
  return `${cssClasses.wrapper.base} ${isChileanTenant ? cssClasses.wrapper.centered : ''}`.trim();
};

/**
 * Generates list CSS classes based on tenant and content context
 *
 * @param {boolean} isChileanTenant - Whether the current tenant is Chilean
 * @param {boolean} hasPreviousParagraphs - Whether there are paragraphs before the list
 * @returns {string} Space-separated CSS class names
 */
export const getListClasses = (isChileanTenant, hasPreviousParagraphs) => {
  const listClasses = [cssClasses.list.base];

  if (!isChileanTenant) {
    listClasses.push(cssClasses.list.modernPadding);
  }

  if (!hasPreviousParagraphs) {
    listClasses.push(cssClasses.list.noTopPadding);
  }

  return listClasses.join(' ').trim();
};

/**
 * Determines whether a divider should be shown based on tenant rules and position
 *
 * @param {boolean} isChileanTenant - Whether the current tenant is Chilean
 * @param {number} sectionIndex - Index of the current section
 * @param {number} totalSections - Total number of sections
 * @param {boolean} hasSecondParagraph - Whether the section has a second paragraph
 * @returns {boolean} Whether to show the divider
 */
export const getShouldShowDivider = (isChileanTenant, sectionIndex, totalSections, hasSecondParagraph) => {
  if (isChileanTenant) {
    return hasSecondParagraph;
  }

  return sectionIndex !== totalSections - 1;
};

/**
 * Generates unique, sanitized keys for list items to prevent React warnings
 *
 * @param {string} sectionTitle - Title of the section containing the list
 * @param {string} itemContent - Content of the list item
 * @returns {string} Unique, sanitized key for the list item
 */
export const generateListItemKey = (sectionTitle, itemContent) => {
  const sectionPrefix = sectionTitle || 'section';
  const itemKey = itemContent.substring(0, 20);
  const sanitizedKey = itemKey.replace(/[^a-zA-Z0-9]/g, '_');

  return `${sectionPrefix}_${sanitizedKey}`;
};

/**
 * Validates that all required parameters are present for content rendering
 *
 * @param {string} regionCode - Region code for tenant identification
 * @param {object} labels - Labels object for localized content
 * @param {string} store - Store identifier
 * @returns {boolean} Whether all required parameters are valid
 */
export const validateContentParameters = (regionCode, labels, store) => {
  return !!(regionCode && labels && typeof labels === 'object' && store);
};

/**
 * Gets the appropriate icon class based on content type and tenant
 *
 * @param {boolean} hasIcon - Whether the content should display an icon
 * @returns {string|null} CSS class for the icon or null if no icon
 */
export const getIconClass = (hasIcon) => {
  return hasIcon ? cssClasses.icon.base : null;
};

/**
 * Sanitizes and validates HTML content for safe rendering
 *
 * @param {string} htmlContent - HTML content to sanitize
 * @returns {string} Sanitized HTML content
 */
export const sanitizeHtmlContent = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return '';
  }

  return htmlContent.trim();
};
